/**
 * Lettura dei ruoli dal JWT (`realm_access.roles`) per decidere **prima** di chiamare le API
 * a quale audience appartiene l'utente autenticato.
 *
 * Perché serve: `/api/v1/cittadino/**` è riservato dal backend a `ROLE_cittadino`
 * (`SecurityConfigApi.java`, matcher `/api/v1/cittadino/profilo/**`), quindi un operatore di
 * backoffice che accede a questa app prende 403 sul GET profilo — e il 403 sul profilo è, per
 * progetto, un "accesso negato all'app" che chiude la sessione. Discriminare sul token evita di
 * fare una chiamata che si sa già negata, senza toccare la difesa a valle: il 403 **imprevisto**
 * (ruolo non riconosciuto, nessun ruolo) continua a innescare il logout come prima.
 *
 * ## Forma delle stringhe
 * Keycloak mette in `realm_access.roles` le authority **già prefissate e minuscole**
 * (`ROLE_cittadino`, `ROLE_op_pratica`, ...): sono gli stessi valori di `AuthorityType.getAuthority()`
 * del backend, usati nei matcher `hasAuthority(...)`. Il realm è condiviso con la dashboard, che
 * normalizza allo stesso modo (`civeshub-dashboard/src/app/core/auth/jwt-claims.ts`: richiede il
 * prefisso `ROLE_` e fa `toUpperCase()` sul resto per risolvere l'enum).
 *
 * L'array contiene anche ruoli tecnici di Keycloak non nostri (`user`, `manage-account`,
 * `offline_access`): tutto ciò che non è un'authority riconosciuta viene **ignorato**, non
 * interpretato.
 */

/** Prefisso con cui Keycloak espone le authority nel claim `realm_access.roles`. */
const ROLE_PREFIX = 'ROLE_';

/**
 * Authority del cittadino: è l'unico ruolo per cui l'app carica il profilo
 * `/api/v1/cittadino/profilo`.
 */
export const RUOLO_CITTADINO = 'ROLE_cittadino';

/**
 * Authority di backoffice ammesse in questa app come **operatori** (audience del submit per conto
 * di un cittadino). Sono utenti che restano loggati ma NON hanno un profilo cittadino da caricare.
 *
 * Elenco chiuso e volutamente ristretto: un ruolo di backoffice non presente qui (es.
 * `ROLE_esercente`, `ROLE_op_scuola`) non è previsto in questa app e deve continuare a seguire
 * il percorso di prima — chiamata al profilo, 403, logout.
 */
export const RUOLI_OPERATORE_AMMESSI: readonly string[] = [
  'ROLE_superadmin',
  'ROLE_op_comune',
  'ROLE_op_pratica',
];

/**
 * Confronto case-insensitive sulla parte dopo `ROLE_`, per non essere fragili a una futura
 * differenza di case nella configurazione del realm. Una stringa senza il prefisso non è
 * un'authority nostra e non viene mai considerata equivalente.
 */
function stessaAuthority(raw: string, authority: string): boolean {
  if (!raw.startsWith(ROLE_PREFIX)) {
    return false;
  }
  return raw.toUpperCase() === authority.toUpperCase();
}

/** `true` se fra i ruoli del token c'è l'authority del cittadino. */
export function isCittadino(roles: readonly string[] | null | undefined): boolean {
  return (roles ?? []).some((r) => stessaAuthority(r, RUOLO_CITTADINO));
}

/**
 * `true` se il token porta **almeno una** delle authority di backoffice ammesse in questa app.
 *
 * Non è l'opposto di {@link isCittadino}: un token senza ruoli riconosciuti non è né cittadino
 * né operatore, e va trattato come prima (chiamata al profilo → 403 → logout).
 */
export function isOperatoreAmmesso(roles: readonly string[] | null | undefined): boolean {
  return (roles ?? []).some((raw) =>
    RUOLI_OPERATORE_AMMESSI.some((authority) => stessaAuthority(raw, authority)),
  );
}

/**
 * Decide se saltare il GET `/api/v1/cittadino/profilo`.
 *
 * Si salta **solo** quando l'utente è un operatore ammesso e NON è un cittadino: se il token
 * porta entrambe le authority (utente con doppio ruolo) vince il cittadino, così l'esperienza
 * del cittadino non cambia in nessun caso.
 */
export function deveSaltareProfiloCittadino(roles: readonly string[] | null | undefined): boolean {
  return !isCittadino(roles) && isOperatoreAmmesso(roles);
}
